<div class="sheet">
  <button type="button" class="dismiss" (click)="close()" aria-label="Close">&times;</button>

  <app-space-claim-header [title]="event.title"></app-space-claim-header>

  <div class="body">
    <div class="hero">
      <img class="logo" *ngIf="event.logoUrl" [src]="event.logoUrl" [alt]="event.title" />
      <div class="hero-text">
        <p class="kicker">{{ event.tag }}</p>
        <button type="button" class="edit-toggle" *ngIf="!editing" (click)="startEdit()">
          Edit details
        </button>
        <p class="lede">
          {{ event.type }}{{ event.isPrivate ? ' · private' : ' · public' }} ·
          {{ event.memberCount }} member{{ event.memberCount === 1 ? '' : 's' }}
        </p>
        <p class="lede" *ngIf="event.owner; else noOwner">Run by {{ event.owner.name }}</p>
        <ng-template #noOwner><p class="lede warn">Nobody owns this space</p></ng-template>
      </div>
    </div>

    <!-- The QR sits first: it is the one thing here that is meant to be used,
         not read. Everything below it is reference. -->
    <app-form-section title="Join this space">
      <p class="section-lede">
        Scan to register yourself into <strong>{{ event.title }}</strong>. This is the join
        code, not the claim code — it adds whoever scans it as a member and leaves
        ownership alone.
      </p>
      <app-qr-code-display
        [isGeneratingQR]="isGeneratingQR"
        [qrCodeData]="qrCodeData"
        [deepLinkUrl]="joinUrl"
        [qrError]="qrError"
        (onRetry)="buildQrCode()"
      ></app-qr-code-display>
    </app-form-section>

    <!-- Reading the space. -->
    <ng-container *ngIf="!editing">
      <app-form-section title="Name and description">
        <ng-container *ngTemplateOutlet="rows; context: { $implicit: identity }"></ng-container>
      </app-form-section>

      <app-form-section title="Dates and registration">
        <ng-container *ngTemplateOutlet="rows; context: { $implicit: schedule }"></ng-container>
      </app-form-section>

      <app-form-section title="Where it happens">
        <ng-container *ngTemplateOutlet="rows; context: { $implicit: place }"></ng-container>
      </app-form-section>

      <app-form-section title="Lobby and people">
        <ng-container *ngTemplateOutlet="rows; context: { $implicit: lobby }"></ng-container>
      </app-form-section>

      <app-form-section title="Logo, links and files">
        <ng-container *ngTemplateOutlet="rows; context: { $implicit: assets }"></ng-container>
      </app-form-section>

      <app-form-section title="Ownership and history">
        <ng-container *ngTemplateOutlet="rows; context: { $implicit: claim }"></ng-container>
      </app-form-section>
    </ng-container>

    <!-- Changing it. Same fields, validators and payload as the claim wizard;
         the logo and brochure use the wizard's own upload components. -->
    <form class="edit" *ngIf="editing && form" [formGroup]="form" (ngSubmit)="save()">
      <div class="notice warn">
        You are editing somebody else’s space. Changes reach
        {{ event.memberCount }} member{{ event.memberCount === 1 ? '' : 's' }} in the app
        immediately, and the organizer is not told.
      </div>

      <app-form-section title="Logo">
        <app-logo-upload
          [eventId]="event.id"
          [currentLogoUrl]="form.get('logoUrl')?.value"
          [existingImageLoader]="loadExistingLogo"
          (logoUploaded)="onLogoUploaded($event)"
          (logoRemoved)="onLogoRemoved()"
        ></app-logo-upload>
        <p class="help">
          A wide logo opens fitted whole inside the square, with margins in its own
          background colour, so nothing is cut. Zoom in to crop it tighter instead.
        </p>
      </app-form-section>

      <app-form-section title="Name and description">
        <div class="field">
          <label for="title">Event name *</label>
          <input id="title" type="text" formControlName="title" maxlength="40" />
          <p class="err" *ngIf="fieldError('title')">{{ fieldError('title') }}</p>
          <p class="help">The name attendees see everywhere in the app</p>
        </div>
        <div class="field">
          <label for="description">Description</label>
          <textarea id="description" formControlName="description" rows="6"></textarea>
        </div>
        <div class="field">
          <label for="websiteUrl">Website URL</label>
          <input id="websiteUrl" type="url" formControlName="websiteUrl" placeholder="https://example.com" />
          <p class="err" *ngIf="fieldError('websiteUrl')">{{ fieldError('websiteUrl') }}</p>
        </div>
      </app-form-section>

      <app-form-section title="Preferences">
        <div class="field">
          <label for="type">Event type</label>
          <select id="type" formControlName="type">
            <option value="Event">Event — a scheduled gathering</option>
            <option value="Community">Community — ongoing, no dates</option>
          </select>
        </div>
        <div class="field">
          <label for="lobbyMode">Lobby mode</label>
          <select id="lobbyMode" formControlName="lobbyMode">
            <option value="GroupChat">Group chat — everyone talks in one room</option>
            <option value="Feed">Feed — posts, not conversation</option>
          </select>
        </div>
        <div class="field check">
          <label><input type="checkbox" formControlName="isPrivate" /> Private — joining needs approval</label>
        </div>
        <div class="field">
          <label for="participantsLimit">Attendee limit</label>
          <input id="participantsLimit" type="number" min="0" formControlName="participantsLimit" />
          <p class="help">{{ event.memberCount }} have joined so far. Leave empty for no limit.</p>
        </div>
      </app-form-section>

      <app-form-section title="Dates and registration">
        <div class="field-row">
          <div class="field">
            <label for="startDate">Start date</label>
            <input id="startDate" type="date" formControlName="startDate" />
          </div>
          <div class="field">
            <label for="endDate">End date</label>
            <input id="endDate" type="date" formControlName="endDate" />
            <p class="err" *ngIf="fieldError('endDate') && !form.get('endDate')?.errors?.['eventTooLong']">{{ fieldError('endDate') }}</p>
            <app-event-too-long-notice [form]="form"></app-event-too-long-notice>
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label for="registrationStart">Registration opens</label>
            <input id="registrationStart" type="date" formControlName="registrationStart" />
          </div>
          <div class="field">
            <label for="registrationEnd">Registration closes</label>
            <input id="registrationEnd" type="date" formControlName="registrationEnd" />
          </div>
        </div>
      </app-form-section>

      <app-form-section title="Where it happens">
        <div class="field">
          <label for="venueName">Venue</label>
          <input id="venueName" type="text" formControlName="venueName" placeholder="Hotel Bayerischer Hof" />
        </div>
        <div class="field">
          <label for="locationDescription">Address</label>
          <input id="locationDescription" type="text" formControlName="locationDescription" placeholder="Promenadeplatz 2-6, Munich" />
          <p class="help">
            Changing either re-pins the map from the address. Clearing both removes the pin.
          </p>
        </div>
      </app-form-section>

      <app-form-section title="Brochure">
        <app-agenda-upload
          [eventId]="event.id"
          [currentUrl]="form.get('brochureUrl')?.value"
          (agendaUploaded)="onAgendaUploaded($event)"
          (agendaRemoved)="onAgendaRemoved()"
        ></app-agenda-upload>
      </app-form-section>

      <div class="notice bad" *ngIf="saveError">{{ saveError }}</div>

      <div class="actions">
        <button type="button" class="ghost" (click)="cancelEdit()" [disabled]="saving">Cancel</button>
        <button type="submit" class="primary" [disabled]="saving">
          {{ saving ? 'Saving…' : 'Review and save…' }}
        </button>
        <span class="help">You will see exactly what changes before anything is saved.</span>
      </div>
    </form>
  </div>
</div>

<!-- One row renderer for every section, so "not set" looks the same everywhere
     and a blank that matters can say so. -->
<ng-template #rows let-list>
  <dl class="spec">
    <ng-container *ngFor="let row of list">
      <dt>{{ row.label }}</dt>
      <dd>
        <ng-container *ngIf="row.value; else empty">
          <a *ngIf="row.href; else plain" [href]="row.href" target="_blank" rel="noopener">{{ row.value }}</a>
          <ng-template #plain>{{ row.value }}</ng-template>
        </ng-container>
        <ng-template #empty>
          <span class="unset" [class.warn]="row.warnWhenEmpty">Not set</span>
        </ng-template>
        <span class="hint" *ngIf="row.hint">{{ row.hint }}</span>
      </dd>
    </ng-container>
  </dl>
</ng-template>
